import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { allocationCalculatorAPI, portfolioRegistryAPI, trackActivity } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePreview } from '../../contexts/PreviewContext';
import BlendBuilder from './components/BlendBuilder';
import AllocationTable from './components/AllocationTable';
import {
  buildAllocation, DIRECT_ASSET_CLASSES, CUSTOM_FUND_NOTE, CURRENT_WEIGHTS_NOTE,
} from './utils/allocationMath';
import { fetchCalculatorDisclaimer } from './utils/disclaimer';
import { exportAllocationToExcel } from './exportToExcel';
import './AllocationCalculatorApp.css';

// Client-facing allocation calculator: total $ + retained cash/TDs + a blend of
// the user's domain-visible portfolio vehicles, APL-approved funds and direct
// asset-class weights → a PDS-style allocation table (sector / fund / APIR / % / $).
// Portfolio weights are the effective TARGET weights, matching the trading app.
// Drummond staff (not while previewing as a client) can also add a saved
// Portfolio Lab portfolio, picked Client → Portfolio (Aaron 2026-10-13).
const AllocationCalculatorApp = () => {
  const { user } = useAuth();
  const { isPreviewMode } = usePreview();
  const showLab = !!user?.is_drummond_user && !isPreviewMode;

  const [registry, setRegistry] = useState(null);
  const [vehicles, setVehicles] = useState(null);   // cascade-map vehicles grouped by platform
  const [funds, setFunds] = useState([]);
  const [labPortfolios, setLabPortfolios] = useState(null);   // Drummond only; null = not loaded
  const [loadError, setLoadError] = useState('');

  const [totalAmount, setTotalAmount] = useState('');
  // Cash and term deposits are presented as separate fields/rows but behave
  // identically: both held off the top, both defensive (Aaron 2026-08-25).
  const [retainedAmount, setRetainedAmount] = useState('');   // Retained Cash $ (target)
  const [tdAmount, setTdAmount] = useState('');               // Term Deposits $ (target)
  // Comparison mode (Aaron 2026-09-10): a Current side entered beside the
  // target — current % per component and current $ per retained item. The
  // current entries are kept (hidden) while the toggle is off.
  const [comparison, setComparison] = useState(false);
  const [retainedCurrent, setRetainedCurrent] = useState('');
  const [tdCurrent, setTdCurrent] = useState('');
  const [components, setComponents] = useState([
    { id: 1, type: 'portfolio', ref: '', share: 100, current: 100 },
  ]);

  const [holdingsByCode, setHoldingsByCode] = useState({});   // code -> rows | null (failed)
  const [loadingCodes, setLoadingCodes] = useState({});
  const [labById, setLabById] = useState({});         // lab id -> {name, geared, unmatched, holdings} | null (failed)
  const [loadingLab, setLoadingLab] = useState({});

  useEffect(() => {
    trackActivity('app_access', 'allocation_calculator');
    // Vehicles come from the cascade map (platform → portfolio code) so white-label
    // platform vehicles are selectable; the registry supplies only the asset classes.
    allocationCalculatorAPI.getVehicles()
      .then(setVehicles)
      .catch(() => setLoadError('Failed to load the portfolio list.'));
    portfolioRegistryAPI.getRegistry('allocation_calculator')
      .then(setRegistry)
      .catch(() => {});
    allocationCalculatorAPI.getFunds()
      .then((d) => setFunds(d.funds || []))
      .catch(() => setFunds([]));
  }, []);

  // Saved Portfolio Lab portfolios — fetched only for Drummond staff; the
  // endpoint itself also refuses anyone else.
  useEffect(() => {
    if (!showLab || labPortfolios !== null) return;
    allocationCalculatorAPI.getLabPortfolios()
      .then((d) => setLabPortfolios(d.portfolios || []))
      .catch(() => {
        setLabPortfolios([]);
        setLoadError('Failed to load the Portfolio Lab portfolios.');
      });
  }, [showLab, labPortfolios]);

  const labNameById = useMemo(
    () => Object.fromEntries((labPortfolios || []).map((p) => [String(p.id), p.name])),
    [labPortfolios],
  );

  // Flat vehicle list for the Series → Portfolio → Platform cascade.
  const vehicleList = useMemo(() => {
    if (!vehicles?.platforms) return [];
    return vehicles.platforms.flatMap((p) =>
      (p.vehicles || []).map((v) => ({ ...v, platform: p.label })));
  }, [vehicles]);

  const nameByCode = useMemo(() => {
    const m = {};
    vehicleList.forEach((v) => { m[v.code] = v.name; });
    return m;
  }, [vehicleList]);

  // Direct-allocation picks: the live taxonomy intersected with the curated
  // client-facing list — internal labels (FX, Global Alpha - G/D, …) never
  // reach the dropdown.
  const assetClassOptions = useMemo(
    () => (registry?.broad_asset_classes || [])
      .map((b) => b.name)
      .filter((n) => DIRECT_ASSET_CLASSES.includes(n)),
    [registry],
  );

  // Manual-component asset classes: only the broad classes the AP List actually
  // uses, derived live from the loaded fund list, falling back to the direct-
  // pick list if the AP List failed to load. Ordered by the registry taxonomy.
  const manualAssetClassOptions = useMemo(() => {
    const present = [...new Set((funds || []).map((f) => f.asset_class_group).filter(Boolean))];
    if (present.length === 0) return assetClassOptions;
    const order = (registry?.broad_asset_classes || []).map((b) => b.name);
    const rank = (n) => { const i = order.indexOf(n); return i < 0 ? Number.MAX_SAFE_INTEGER : i; };
    return present.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [funds, registry, assetClassOptions]);

  const selectedCodes = useMemo(
    () => [...new Set(components.filter((c) => c.type === 'portfolio' && c.ref).map((c) => c.ref))],
    [components],
  );

  // Fetch target holdings for newly selected portfolios (one CSV call, cached by
  // code). Event-driven: runs from the components-change handler, not an effect.
  // requestedRef remembers every code ever fetched so re-selects don't refetch.
  const requestedRef = React.useRef(new Set());
  const fetchHoldings = useCallback((codes) => {
    const missing = codes.filter((c) => !requestedRef.current.has(c));
    if (missing.length === 0) return;
    missing.forEach((c) => requestedRef.current.add(c));
    setLoadingCodes((prev) => ({ ...prev, ...Object.fromEntries(missing.map((c) => [c, true])) }));
    allocationCalculatorAPI.getHoldings(missing)
      .then((data) => {
        const byCode = Object.fromEntries(missing.map((c) => [c, []]));
        (data.holdings || []).forEach((h) => {
          missing.forEach((code) => {
            const w = (h.weights || {})[code];
            if (w == null) return;
            byCode[code].push({
              asset_name: h.asset_name,
              apir_code: h.apir_code || '',
              tr_code: h.tr_code || '',
              asset_class_group: h.asset_class_group || '',
              weight: w,
              mer_pct: h.mer_pct == null ? null : h.mer_pct,
            });
          });
        });
        setHoldingsByCode((prev) => ({ ...prev, ...byCode }));
      })
      .catch(() => {
        // Allow a retry on re-select after a network failure.
        missing.forEach((c) => requestedRef.current.delete(c));
        setHoldingsByCode((prev) => ({
          ...prev, ...Object.fromEntries(missing.map((c) => [c, null])),
        }));
      })
      .finally(() => {
        setLoadingCodes((prev) => ({
          ...prev, ...Object.fromEntries(missing.map((c) => [c, false])),
        }));
      });
  }, []);

  // Same pattern for Lab portfolios (keyed by id). An id the endpoint no longer
  // returns (deleted in Lab since the list loaded) gets an empty holdings set,
  // so the allocation reports "No target holdings found" by name.
  const requestedLabRef = React.useRef(new Set());
  const fetchLabHoldings = useCallback((ids) => {
    const missing = ids.filter((id) => !requestedLabRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedLabRef.current.add(id));
    setLoadingLab((prev) => ({ ...prev, ...Object.fromEntries(missing.map((id) => [id, true])) }));
    allocationCalculatorAPI.getLabHoldings(missing)
      .then((data) => {
        const got = data.portfolios || {};
        setLabById((prev) => ({
          ...prev,
          ...Object.fromEntries(missing.map((id) => [id, got[id] || {
            name: labNameById[id] || id, geared: false, unmatched: [], holdings: [],
          }])),
        }));
      })
      .catch(() => {
        missing.forEach((id) => requestedLabRef.current.delete(id));
        setLabById((prev) => ({ ...prev, ...Object.fromEntries(missing.map((id) => [id, null])) }));
      })
      .finally(() => {
        setLoadingLab((prev) => ({ ...prev, ...Object.fromEntries(missing.map((id) => [id, false])) }));
      });
  }, [labNameById]);

  const handleComponentsChange = useCallback((next) => {
    setComponents(next);
    const codes = [...new Set(next.filter((c) => c.type === 'portfolio' && c.ref).map((c) => c.ref))];
    if (codes.length) fetchHoldings(codes);
    const labIds = [...new Set(next.filter((c) => c.type === 'lab' && c.ref).map((c) => c.ref))];
    if (labIds.length) fetchLabHoldings(labIds);
  }, [fetchHoldings, fetchLabHoldings]);

  // Comparison helpers: copy one side onto the other (weights AND retained $)
  // so the adviser only has to edit the differences.
  const copyTargetToCurrent = useCallback(() => {
    setComponents((cs) => cs.map((c) => ({ ...c, current: c.share || 0 })));
    setRetainedCurrent(retainedAmount);
    setTdCurrent(tdAmount);
  }, [retainedAmount, tdAmount]);
  const copyCurrentToTarget = useCallback(() => {
    setComponents((cs) => cs.map((c) => ({ ...c, share: c.current || 0 })));
    setRetainedAmount(retainedCurrent);
    setTdAmount(tdCurrent);
  }, [retainedCurrent, tdCurrent]);

  const total = Math.round(parseFloat(totalAmount) || 0);
  // Cash and TDs are separate $ fields beside the total, both held off the top.
  const retainedCash = Math.round(parseFloat(retainedAmount) || 0);
  const retainedTd = Math.round(parseFloat(tdAmount) || 0);
  const retainedCashCurrent = Math.round(parseFloat(retainedCurrent) || 0);
  const retainedTdCurrent = Math.round(parseFloat(tdCurrent) || 0);
  const selectedLabIds = useMemo(
    () => [...new Set(components.filter((c) => c.type === 'lab' && c.ref).map((c) => c.ref))],
    [components],
  );
  const loading = selectedCodes.some((c) => loadingCodes[c])
    || selectedLabIds.some((id) => loadingLab[id]);
  const fetchFailed = [
    ...selectedCodes.filter((c) => holdingsByCode[c] === null).map((c) => nameByCode[c] || c),
    ...selectedLabIds.filter((id) => labById[id] === null).map((id) => labNameById[id] || id),
  ];

  // A component counts when it has a weight on ANY visible side — in comparison
  // mode a fund being sold out (target 0, current > 0) must stay in the table.
  const activeComponents = useMemo(
    () => components.filter((c) => c.ref
      && ((c.share || 0) > 0 || (comparison && (c.current || 0) > 0))),
    [components, comparison],
  );

  const allocation = useMemo(() => {
    if (!(total > 0) || loading || activeComponents.length === 0) return null;
    const cleanHoldings = {};
    Object.entries(holdingsByCode).forEach(([code, rows]) => {
      if (rows) cleanHoldings[code] = rows;
    });
    const cleanLab = {};
    Object.entries(labById).forEach(([id, lab]) => {
      if (lab) cleanLab[id] = lab;
    });
    return buildAllocation({
      total,
      retainedItems: [
        { name: 'Retained Cash', amount: retainedCash, current: retainedCashCurrent },
        { name: 'Term Deposits', amount: retainedTd, current: retainedTdCurrent },
      ],
      components: activeComponents,
      holdingsByCode: cleanHoldings,
      nameByCode,
      labById: cleanLab,
      growthClasses: registry?.growth_broad_classes || [],
      classOrder: (registry?.broad_asset_classes || []).map((b) => b.name),
      comparison,
    });
  }, [total, retainedCash, retainedTd, retainedCashCurrent, retainedTdCurrent, comparison,
    activeComponents, holdingsByCode, labById, loading, nameByCode, registry]);

  // "Allocation basis" line on the PDF/Excel. In comparison mode each component
  // reads "current% → target%" so a sold-out line (target 0) is still explained.
  const blendSummary = useMemo(() => activeComponents.map((c) => {
    const w = comparison ? `${c.current || 0}% → ${c.share || 0}%` : `${c.share || 0}%`;
    if (c.type === 'portfolio') return `${w} ${nameByCode[c.ref] || c.ref}`;
    // Lab portfolios print under their own Lab name (Aaron 2026-10-13).
    if (c.type === 'lab') return `${w} ${labById[c.ref]?.name || labNameById[c.ref] || c.ref}`;
    if (c.type === 'fund') return `${w} ${c.ref.fund_name || c.ref.apir_code}`;
    if (c.type === 'manual') return `${w} ${c.ref.fund_name} (manual)`;
    return `${w} ${c.ref} (direct)`;
  }).join(' + '), [activeComponents, nameByCode, labById, labNameById, comparison]);

  const handleExport = useCallback(async () => {
    if (!allocation || allocation.errors.length) return;
    const disclaimer = await fetchCalculatorDisclaimer();
    exportAllocationToExcel({ allocation, total, blendSummary, disclaimer });
  }, [allocation, total, blendSummary]);

  // Server-rendered branded PDF (WeasyPrint, same pipeline as the retirement
  // report). Sends the DISPLAY-READY figures exactly as shown on screen.
  const [pdfBusy, setPdfBusy] = useState(false);
  const handlePdf = useCallback(async () => {
    if (!allocation || allocation.errors.length) return;
    const fmtP = (v) => `${v.toFixed(2)}%`;
    const fmtD = (v) => `$${Math.round(v).toLocaleString()}`;
    const { retainedRows, sectors, totalRow, exposure, comparison: cmp } = allocation;
    const fmtSP = (v) => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
    const sign = (v) => (v > 0 ? 'pos' : v < 0 ? 'neg' : '');
    // Every row-like object carries the same figure keys; the comparison keys
    // are present only when the view is on (the PDF omits Change $ for width).
    const figs = (f) => (cmp ? {
      pct: fmtP(f.pct), dollars: fmtD(f.dollars),
      current_pct: fmtP(f.curPct), current_dollars: fmtD(f.curDollars),
      change_pct: fmtSP(f.changePct), change_sign: sign(f.changePct),
    } : { pct: fmtP(f.pct), dollars: fmtD(f.dollars) });
    const expo = (e) => ({
      growth_width: Math.max(0, Math.min(100, e.growthPct)),
      growth_pct: fmtP(e.growthPct),
      growth_dollars: fmtD(e.growthDollars),
      defensive_pct: fmtP(e.defensivePct),
      defensive_dollars: fmtD(e.defensiveDollars),
    });
    const payload = {
      total_display: fmtD(total),
      blend_summary: blendSummary,
      // Everything dates to the day of output (Aaron 2026-08-25): the weights
      // are always the live effective target set, so no separate as-at date.
      as_at_date: new Date().toLocaleDateString('en-AU'),
      disclaimer: await fetchCalculatorDisclaimer(),
      // Selected vehicle codes — the backend brands the PDF by the portfolio's
      // cascade-map client (white-label logo/colours), like the fact sheets.
      portfolio_codes: selectedCodes,
      comparison: !!cmp,
      retained_rows: (retainedRows || []).map((r) => ({ name: r.name, ...figs(r) })),
      sectors: sectors.map((s) => ({
        label: s.label,
        figures: figs({
          pct: s.subtotalPct, dollars: s.subtotalDollars,
          curPct: s.subtotalCurPct, curDollars: s.subtotalCurDollars,
          changePct: s.changePct, changeDollars: s.changeDollars,
        }),
        rows: s.rows.map((r) => ({
          name: r.name, code: r.code || '', ...figs(r),
          is_direct: !!r.isDirect,
          is_custom: !!r.isCustom,
        })),
      })),
      // Footnotes — adviser-typed manual components only when one is present;
      // the current-weights note whenever the comparison view is on.
      custom_note: sectors.some((s) => s.rows.some((r) => r.isCustom))
        ? CUSTOM_FUND_NOTE : null,
      current_note: cmp ? CURRENT_WEIGHTS_NOTE : null,
      total_row: figs(totalRow),
      exposure: exposure ? {
        ...expo(exposure),
        current: cmp && exposure.current ? expo(exposure.current) : null,
      } : null,
    };
    setPdfBusy(true);
    try {
      const blob = await allocationCalculatorAPI.exportPdf(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'asset_allocation.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setLoadError('Failed to generate the PDF.');
    } finally {
      setPdfBusy(false);
    }
  }, [allocation, total, blendSummary, selectedCodes]);

  const ready = allocation && allocation.errors.length === 0 && allocation.sectors.length > 0;

  return (
    <div className="alloc-calc-app">
      <div className="alloc-header">
        <div className="alloc-header-row">
          <h2>Allocation Calculator</h2>
          <label
            className="alloc-toggle form-check form-switch"
            title="Enter the current weights beside the targets and show the change"
          >
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={comparison}
              onChange={(e) => setComparison(e.target.checked)}
            />
            <span className="form-check-label">Comparison</span>
          </label>
        </div>
        <p className="alloc-subtitle">
          Enter a total investment amount, then build the allocation — a portfolio or blend of
          portfolios, funds and asset classes, plus optional Retained Cash and Term Deposit
          amounts held aside. The table shows the target allocation in percentages and dollars.
          {comparison && (
            ' Comparison is on: enter the current weights beside the target weights to show the change.'
          )}
        </p>
      </div>

      {loadError && <div className="alert alert-danger">{loadError}</div>}
      {fetchFailed.length > 0 && (
        <div className="alert alert-danger">
          Failed to load holdings for {fetchFailed.join(', ')}.
        </div>
      )}

      <BlendBuilder
        totalAmount={totalAmount}
        onTotalChange={setTotalAmount}
        retainedAmount={retainedAmount}
        onRetainedChange={setRetainedAmount}
        tdAmount={tdAmount}
        onTdChange={setTdAmount}
        retainedCurrent={retainedCurrent}
        onRetainedCurrentChange={setRetainedCurrent}
        tdCurrent={tdCurrent}
        onTdCurrentChange={setTdCurrent}
        components={components}
        onComponentsChange={handleComponentsChange}
        vehicles={vehicleList}
        labPortfolios={showLab ? labPortfolios : null}
        funds={funds}
        assetClassOptions={assetClassOptions}
        manualAssetClassOptions={manualAssetClassOptions}
        loading={loading}
        comparison={comparison}
        onCopyTargetToCurrent={copyTargetToCurrent}
        onCopyCurrentToTarget={copyCurrentToTarget}
      />

      {allocation && allocation.errors.length > 0 && (
        <div className="alert alert-warning alloc-errors">
          {allocation.errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}

      {ready && (
        <>
          <div className="alloc-actions">
            <div className="alloc-blend-summary" title="Allocation basis">{blendSummary}</div>
            <div className="alloc-actions-buttons">
              <button className="btn btn-outline-primary btn-sm" onClick={handlePdf} disabled={pdfBusy}>
                {pdfBusy ? 'Generating…' : 'Download PDF'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleExport}>
                Export to Excel
              </button>
            </div>
          </div>
          <AllocationTable allocation={allocation} total={total} />
        </>
      )}

      {loading && <div className="alloc-loading">Loading portfolio targets…</div>}
    </div>
  );
};

export default AllocationCalculatorApp;
